import { useEffect, useId, useState } from "react";
import { Button } from "@/ui/Button";
import { plural } from "@/lib/plural";
import { isActive, useReferenceRuns, useStartReferenceRun, type Run } from "@/app/gradingQueries";
import { useStepAssignment, useUpdateAssignment, type Assignment } from "./filesApi";
import { INPUT } from "./shared";

const HEAD = "mb-[11px] font-mono text-[11px] uppercase tracking-[.06em] text-mut";
const ROW = "flex items-center justify-between gap-2.5";

/** Карточка «автопроверка» вкладки «домашка»: всё — из манифеста задания. */
export function AssignmentCard({ stepId, onThreshold }: { stepId: string; onThreshold?: (n: number) => void }) {
  const q = useStepAssignment(stepId);
  const update = useUpdateAssignment(stepId);
  const inputId = useId();
  const a = q.data;
  const tasks = a?.manifest.tasks.length ?? 0;
  const saved = a?.manifest.threshold ?? 0;
  const [value, setValue] = useState(String(saved));
  const [error, setError] = useState<string | null>(null);

  // Новый манифест (загрузили шаблон, сохранили порог) — поле следует за ним.
  useEffect(() => setValue(String(saved)), [saved, a?.updated_at]);

  const commit = async () => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 1) {
      setError(`Порог — целое число от 1 до ${tasks}.`);
      return;
    }
    if (n === saved) {
      setError(null);
      return;
    }
    try {
      await update.mutateAsync({ threshold: n });
      setError(null);
      onThreshold?.(n);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Порог не сохранился.");
    }
  };

  return (
    <section className="rounded-md border border-bd bg-card p-3.5" data-testid="assignment-card">
      <div className={HEAD}>автопроверка</div>
      {q.status === "pending" && <div className="font-mono text-[11.5px] text-ink2">загружаем задание…</div>}
      {q.status === "error" && (
        <div className="flex flex-col items-start gap-2 text-[12.5px] leading-[1.45] text-ink2" role="alert">
          <span>Задание шага не загрузилось: {q.error.message} Файлы в таблице не пострадали.</span>
          <Button size="sm" onClick={() => void q.refetch()}>
            повторить
          </Button>
        </div>
      )}
      {q.status === "success" && !a && (
        <p className="m-0 text-pretty text-[12.5px] leading-[1.45] text-ink2" data-testid="no-notebook">
          Загрузите ноутбук-шаблон: ячейки с тегами task-N и checks-N станут задачами.
        </p>
      )}
      {a && (
        <div className="flex flex-col gap-2.5 font-mono text-[11.5px]">
          <div className={ROW}>
            <span className="text-mut">задач с assert</span>
            <span className="text-ink2" data-testid="assert-count">
              {tasks} из {tasks}
            </span>
          </div>
          <div className={ROW}>
            <label htmlFor={inputId} className="text-mut">
              порог отправки
            </label>
            <span className="flex items-center gap-1.5 text-ink2">
              <input
                id={inputId}
                type="number"
                min={1}
                max={tasks}
                value={value}
                aria-invalid={error ? true : undefined}
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => void commit()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className={`${INPUT} w-[64px] py-1 text-right font-mono text-[11.5px]`}
                style={error ? { borderColor: "var(--err)" } : undefined}
              />
              из {tasks}
            </span>
          </div>
          {update.isPending && <div className="text-right text-[11px] text-ink2">сохраняем порог…</div>}
          {error && (
            <div className="text-[11px] leading-[1.45] text-err" role="alert">
              {error}
            </div>
          )}
          <div className={ROW}>
            <span className="text-mut">таймаут прогона</span>
            <span className="text-ink2">{a.manifest.timeout_s} с</span>
          </div>
          <div className={ROW}>
            <span className="text-mut">среда прогона</span>
            <span className="text-ink2">CPU · в браузере</span>
          </div>
          <div className="flex items-start justify-between gap-2.5">
            <span className="flex-none text-mut">
              {a.manifest.datasets.length} {plural(a.manifest.datasets.length, "датасет", "датасета", "датасетов")}
            </span>
            {a.manifest.datasets.length > 0 ? (
              <ul className="m-0 flex min-w-0 list-none flex-col items-end gap-1 p-0 text-ink2" data-testid="datasets">
                {a.manifest.datasets.map((d) => (
                  <li key={d.blob_id} className="max-w-full truncate">
                    {d.name}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-right text-ink2">загрузите csv с ролью «данные»</span>
            )}
          </div>
        </div>
      )}
      <ReferenceRun assignment={a ?? null} status={q.status} />
    </section>
  );
}

const sec = (ms: number) => `${(ms / 1000).toFixed(1)} с`;

/** Задачи эталона, которые не прошли: номер и тип ошибки из результата. */
function misses(run: Run) {
  return (run.result?.tasks ?? []).filter((t) => t.status !== "passed");
}

/** «прогнать эталон» (A1, SP3): активна, когда в манифесте есть эталон;
 *  результат последнего прогона, упавшие задачи и предупреждения о пороге. */
function ReferenceRun({ assignment: a, status }: { assignment: Assignment | null; status: "pending" | "error" | "success" }) {
  const loading = status !== "success";
  const hasSolution = !!a?.manifest.files.solution_blob_id;
  const refs = useReferenceRuns(hasSolution ? a!.id : undefined);
  const start = useStartReferenceRun(a?.id ?? "");
  const list = refs.data ?? [];
  const latest = list[0];
  // Между ответом POST и обновлением истории верхний прогон ещё старый — ждём свой.
  const waiting = !!start.data && !list.some((r) => r.id === start.data.id);
  const busy = start.isPending || waiting || isActive(latest);

  // Заблокированная кнопка всегда с причиной (правило 3) — и пока задание грузится.
  const reason =
    status === "pending"
      ? "загружаем задание…"
      : status === "error"
        ? "задание не загрузилось"
        : !a
          ? "сначала загрузите ноутбук-шаблон"
          : !hasSolution
            ? "загрузите ноутбук с ролью «эталон»"
            : busy
              ? "эталон прогоняется…"
              : undefined;
  const disabled = loading || !a || !hasSolution || busy;

  const total = a?.manifest.tasks.length ?? 0;
  const threshold = a?.manifest.threshold ?? 0;
  const done = latest && !isActive(latest) && !busy ? latest : undefined;
  const envError = done && (done.status === "error" || done.result?.env_error) ? (done.result?.env_error?.message ?? "прогон не завершился, результата нет.") : null;
  const missed = done && !envError ? misses(done) : [];
  const nums = missed.map((t) => t.n).join(", ");
  const warnings: string[] = [];
  if (done && !envError) {
    if (done.passed < threshold) warnings.push(`эталон не проходит порог ${threshold} из ${total} — студенты не смогут отправить работу`);
    if (done.passed < done.total) warnings.push(`эталон падает на ${plural(missed.length, "задаче", "задачах", "задачах")} ${nums}`);
  }

  return (
    <div className="mt-3 flex flex-col gap-2 font-mono text-[11.5px]" data-testid="reference-run">
      <Button size="sm" disabled={disabled} reason={reason} onClick={() => a && hasSolution && !busy && start.mutate()}>
        прогнать эталон
      </Button>
      {start.isError && (
        <div className="text-pretty text-[11px] leading-[1.45] text-err" role="alert">
          {start.error.message}
        </div>
      )}
      {refs.status === "error" && (
        <div className="flex flex-col items-start gap-1.5 text-[11px] leading-[1.45] text-ink2" data-testid="reference-load-error">
          <span>Прогоны эталона не загрузились: {refs.error instanceof Error ? refs.error.message : "сервер не ответил."}</span>
          <Button size="sm" onClick={() => void refs.refetch()}>
            повторить
          </Button>
        </div>
      )}
      {hasSolution && refs.status === "success" && list.length === 0 && !busy && (
        <div className="text-[11px] text-ink2" data-testid="reference-empty">
          эталон ещё не прогоняли
        </div>
      )}
      {done && envError && (
        <div className="text-pretty text-[11px] leading-[1.45] text-err" data-testid="reference-env-error">
          ошибка среды: {envError}
        </div>
      )}
      {done && !envError && (
        <div className={done.passed === done.total ? "text-ok" : "text-warn"} data-testid="reference-result">
          эталон: {done.passed}/{done.total} · {sec(done.result?.duration_ms ?? 0)} · прогон #{done.n}
        </div>
      )}
      {missed.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-[11px] text-ink2" data-testid="reference-failures">
          {missed.map((t) => (
            <li key={t.n}>
              задача {t.n} · {t.status === "idle" ? "не запускалась" : t.error_kind || "упала"}
            </li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <div className="flex flex-col gap-1 rounded-[4px] border border-warn bg-warnsoft px-2.5 py-2 text-pretty text-[11px] leading-[1.45] text-ink2" data-testid="reference-warnings">
          {warnings.map((w) => (
            <p key={w} className="m-0">
              <span className="text-warn">⚠</span> {w}
            </p>
          ))}
        </div>
      )}
      {list.length > 1 && (
        <div className="text-[11px] text-ink2" data-testid="reference-history">
          {list.length} {plural(list.length, "прогон", "прогона", "прогонов")} эталона
        </div>
      )}
    </div>
  );
}
